import {SxProps, Theme} from '@mui/material';
import theme from 'src/theme';
import {
    PHONE_BOTTOM_BAR_HEIGHT,
    phoneActionBarSx,
    phonePrimaryButtonSx,
} from 'src/components/mobile/mobileDesign.sx';

/** The button height the canvas draws, and the room the bar needs above the fold. */
const ACTION_HEIGHT = 48;

/**
 * 10px above the buttons and 10px below them.
 *
 * The canvas gives this bar a 22px thumb margin because there it rests on the bottom edge of the
 * screen. Here it rests on the bottom navigation, which carries that margin itself, so a second one
 * would only be a band of empty white between two bars.
 */
export const RECORD_ACTION_BAR_HEIGHT = ACTION_HEIGHT + 10 + 10;

/**
 * Fixed rather than sticky.
 *
 * The token describes a bar pinned to the foot of a record, and on this shell the page itself is
 * what scrolls: a sticky bar would have to be the last child of the page to avoid leaving a gap
 * where it naturally sits, and the actions belong to the card that owns their handlers, not to the
 * end of the page. Fixed puts it in the same place without moving the ownership. Safe here because
 * nothing between this and the viewport is transformed - `pageWrapperSx` and `pageContentSx` are
 * plain flow.
 */
export const barSx: SxProps<Theme> = {
    ...(phoneActionBarSx as object),
    position: 'fixed',
    left: 0,
    right: 0,
    // Above the bottom navigation rather than under it. Both are fixed to the foot of the screen, so
    // at `bottom: 0` the record's actions and the five places people go were handed the same 72px -
    // one of them would simply have been sitting on the other.
    bottom: `${PHONE_BOTTOM_BAR_HEIGHT}px`,
    paddingBottom: '10px',
    zIndex: theme.zIndex.appBar,
    /**
     * A page may offer four actions - a photograph, a name, a profile and a password reset. Three
     * fit; the fourth has to stay reachable rather than being dropped or squeezed under its label.
     *
     * It is the primary action that gives way (see `actionSlotSx`), because its label is the only
     * elastic thing in the row - the others are 48px squares a thumb has to be able to hit. The
     * scrollport stays as the last resort, but its scrollbar is no longer hidden: hiding it is how
     * the pricing week and the stats tiles each ended up holding something nobody could reach, and a
     * fixed bar with no scrollbar and no wheel axis is the same trap on a narrow desktop window.
     */
    overflowX: 'auto',
};

/** What the bar takes from the page beneath it, so the last card is never trapped under it. */
export const spacerSx: SxProps<Theme> = {
    height: `${RECORD_ACTION_BAR_HEIGHT}px`,
    flexShrink: 0,
};

/**
 * One action's place in the bar.
 *
 * The button inside is the page's own - same handler, same label, same permission gate, same modal.
 * Only its geometry is restated, because a 32px toolbar button is not a thumb target. `primary`
 * additionally gives the page's main action the filled blue the canvas puts under the thumb; a
 * destructive action arrives already wearing the brand gradient and is left alone.
 */
export const actionSlotSx = (primary = false): SxProps<Theme> => ({
    display: 'flex',
    // The primary action is the one that gives way when the row is too wide: its label is elastic,
    // while every other slot is a 48px square that a thumb must still be able to hit.
    flexShrink: primary ? 1 : 0,
    minWidth: 0,
    ...(primary ? {flexGrow: 1} : {}),
    '& .MuiButton-root': {
        width: '100%',
        height: ACTION_HEIGHT,
        minHeight: ACTION_HEIGHT,
        px: 2,
        borderRadius: '12px',
        fontSize: '15px',
        fontWeight: 600,
        textTransform: 'none',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: 'block',
        ...(primary
            ? {
                ...(phonePrimaryButtonSx as object),
                color: theme.palette.common.white,
                background: `linear-gradient(180deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
                border: '1px solid transparent',
                '&:hover': {
                    background: `linear-gradient(180deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    border: '1px solid transparent',
                    boxShadow: '0 4px 12px rgba(0, 127, 220, 0.3)',
                },
            }
            : {}),
    },
});

/**
 * A page whose actions arrive as one opaque node.
 *
 * Three of these pages hand their identity card a ready-made `actions` element built by whichever
 * parent knows the permissions - and that element is the same one the desktop header renders, so it
 * cannot be taken apart here to mark one button primary. `display: contents` drops this wrapper out
 * of the layout so each button inside becomes a flex item of the bar in its own right, and only the
 * geometry is restated. Whatever tone a button already carries - the filled blue of "move to
 * operation", the brand gradient of "archive" - it keeps.
 */
export const legacyActionsSx: SxProps<Theme> = {
    display: 'contents',
    '& .MuiButton-root': {
        flex: '1 1 auto',
        minWidth: 'fit-content',
        height: ACTION_HEIGHT,
        minHeight: ACTION_HEIGHT,
        px: 2,
        borderRadius: '12px',
        fontSize: '15px',
        fontWeight: 600,
        textTransform: 'none',
        whiteSpace: 'nowrap',
    },
};

/**
 * An action with no room for its word: the 48px outlined square the canvas puts beside the primary.
 *
 * Used where the page's own button is already an icon plus a label and the label is the one that can
 * go - a photograph, a logo. The label stays on the button as its accessible name.
 */
export const squareActionSx: SxProps<Theme> = {
    flexShrink: 0,
    '& .MuiButton-root': {
        minWidth: ACTION_HEIGHT,
        width: ACTION_HEIGHT,
        height: ACTION_HEIGHT,
        p: 0,
        borderRadius: '12px',
        '& .MuiButton-startIcon': {
            margin: 0,
        },
    },
};
